/**
 * Wall art product page extras (sections/c365-product.liquid with line = wall):
 *  - "More from {artist}": cards fetched from the search page via section c365-wall-row;
 *  - "Read more" on a long artist bio;
 *  - the price under each size box, for the selected product type (design "Product page - Wall Art");
 *  - "See it on your wall" (brief §C): the artwork at the selected size, type and frame, to scale above an 84″
 *    sofa, on a wall in one of 7 colors or a custom one. The color is shared with the collection picker
 *    (localStorage "c365.wall", assets/c365-wall.js). Redraws on c365:change from <c365-product>.
 */
(() => {
  const pdp = document.querySelector('c365-product');
  if (!pdp) return;
  // Liquid's t filter HTML-escapes ' " & < >; the strings here go to textContent / template literals, so undo it
  const D = (s) => (typeof s === 'string' ? s.replace(/&(#39|#34|quot|amp|lt|gt);/g, (m, e) => ({ '#39': "'", '#34': '"', quot: '"', amp: '&', lt: '<', gt: '>' })[e]) : s);
  // text in the visitor's language: window.C365_T (c365.js.wall.* and "label.<English value>", filled by
  // sections/c365-product.liquid), English without it
  const T = (k, f, vars) =>
    String(D(window.C365_T && window.C365_T[k]) || f).replace(/\{(\w+)\}/g, (m, n) => (vars && n in vars ? vars[n] : m));
  const L = (s) => D(s && window.C365_T && window.C365_T[`label.${s}`]) || s;
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // ---- More from this artist, More {topic} (snippets/c365-wall-related.liquid) ----
  pdp.querySelectorAll('[data-artist-row]').forEach((row) => {
    // after the page has settled: it sits below the fold
    const load = () =>
      fetch(row.dataset.url)
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const items = [...doc.querySelectorAll('.c365-wall-row__item')].filter((el) => el.dataset.handle !== row.dataset.exclude);
          if (items.length < 2) return; // one other artwork isn't a row
          row.querySelector('[data-artist-grid]').append(...items.slice(0, 8));
          row.hidden = false;
        })
        .catch(() => {});
    'requestIdleCallback' in window ? requestIdleCallback(load, { timeout: 3000 }) : setTimeout(load, 1500);
  });

  // ---- summary of the choice above the button: "24″ × 36″ Canvas Print · 1.25″ deep · Ready to hang" ----
  const summary = pdp.querySelector('[data-wall-summary]');
  if (summary) {
    const render = () => {
      if (!pdp.size) return;
      const [w, h] = pdp.size.split('x');
      const type = pdp.isPrint ? L('Framed Print') : pdp.isFramed ? L('Framed Canvas') : L('Canvas Print');
      const parts = [`${w}″ × ${h}″ ${type}`];
      if (pdp.hasFrame && pdp.frame) parts.push(T('wall.frame_value', '{frame} frame', { frame: L(pdp.frame) }));
      if (pdp.isPrint && pdp.mat) parts.push(L(pdp.mat));
      if (!pdp.isPrint && !pdp.isFramed) parts.push(T('wall.deep', '{n}″ deep', { n: '1.25' }));
      parts.push(T('wall.ready_to_hang', 'Ready to hang'));
      summary.textContent = parts.join(' · ');
      summary.hidden = false;
    };
    pdp.addEventListener('c365:change', render);
    let n = 0;
    const firstRender = () => (pdp.size ? render() : ++n < 50 && setTimeout(firstRender, 100));
    firstRender();
  }

  // ---- price under each size (c365.options row: canvas / framed / print), for the selected product type ----
  // USD in the metafield: converted at the market's rate, the same way the options app prices the cart line
  const sizePrices = () => {
    const rows = pdp.opts?.sizes;
    if (!rows) return;
    pdp.querySelectorAll('.c365-size[data-size]').forEach((b) => {
      const row = rows.find((r) => r.size === b.dataset.size);
      const p = Number(row?.[pdp.ptype]);
      let el = b.querySelector('.c365-size__price');
      if (!el) {
        el = document.createElement('span');
        el.className = 'c365-size__price';
        b.querySelector('.c365-size__label')?.after(el);
      }
      el.textContent = p > 0 ? pdp.usdMoney(p) : '';
    });
  };
  pdp.addEventListener('c365:change', sizePrices);
  // first paint: <c365-product> reads its options after this script may have run
  let tries = 0;
  const first = () => (pdp.opts && pdp.ptype ? sizePrices() : ++tries < 50 && setTimeout(first, 100));
  first();

  // ---- See it on your wall ----
  const openBtn = pdp.querySelector('[data-wall-view-open]');
  if (openBtn) {
    const WALLS = [['White', '#f3f1ec'], ['Warm gray', '#cdc5b9'], ['Cool gray', '#b8bdc2'], ['Beige', '#e4d6c0'],
      ['Sage', '#b4bea4'], ['Navy', '#2f3e55'], ['Charcoal', '#3a3c3f']];
    const FRAMES = { Black: '#1d1d1d', White: '#f5f5f2', Walnut: '#5b3b25', Natural: '#c9a77a', 'Rustic White': '#e6e0d4',
      Gold: '#c8a14a', Silver: '#b9bcc0' };
    // the view is 5:3; in inches: floor strip 8, sofa 84 × 32 on it, the art 8 above the sofa, 8 free above the art.
    // 150″ of wall across (as the size guide) unless a tall artwork needs more
    const FLOOR = 8, SOFA_W = 84, SOFA_H = 32, GAP = 8;
    const KEY = 'c365.wall';
    const read = () => { try { return localStorage.getItem(KEY); } catch (e) { return null; } };
    const write = (v) => { try { localStorage.setItem(KEY, v); } catch (e) { /* private mode */ } };
    const data = JSON.parse(pdp.querySelector('[data-product-json]')?.textContent || '{}');
    const el = document.createElement('div');
    el.className = 'c365-wv-backdrop';
    el.hidden = true;
    const title = esc(T('wall.see_on_wall', 'See it on your wall'));
    el.innerHTML = `
      <div class="c365-wv" role="dialog" aria-modal="true" aria-label="${title}">
        <div class="c365-wv__head">
          <b>${title}</b><span data-wv-sub></span>
          <button type="button" class="c365-btn c365-btn-ghost c365-btn-icon" data-wv-close aria-label="${esc(T('wall.close', 'Close'))}">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="c365-wv__wall" data-wv-wall>
          <div class="c365-wv__art" data-wv-art><div class="c365-wv__mat"><img alt="" data-wv-img></div></div>
          <div class="c365-wv__sofa" aria-hidden="true"><i></i><i></i><i></i><i></i></div>
          <div class="c365-wv__floor"></div>
        </div>
        <div class="c365-wv__swatches" role="radiogroup" aria-label="${esc(T('wall.wall_color', 'Wall color'))}">
          ${WALLS.map(([n, h]) => `<button type="button" class="c365-wv__sw" data-wv-color="${h}" data-name="${n}" role="radio" aria-label="${esc(L(n))}" title="${esc(L(n))}"><span style="background:${h}"></span></button>`).join('')}
          <label class="c365-wv__custom" title="${esc(T('wall.custom_color', 'Custom color'))}">${esc(T('wall.custom', 'Custom'))} <input type="color" value="#e9e4dc" data-wv-custom></label>
        </div>
      </div>`;
    document.body.append(el);
    const $ = (s) => el.querySelector(s);
    const wall = $('[data-wv-wall]');
    const art = $('[data-wv-art]');
    const img = $('[data-wv-img]');
    let ratio = 0; // art width / height, from the image
    img.addEventListener('load', () => { ratio = img.naturalWidth / img.naturalHeight; draw(); });

    const setWall = (hex, name) => {
      wall.style.setProperty('--wv-wall', hex);
      el.querySelectorAll('[data-wv-color]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.wvColor === hex)));
      if (name) write(name);
    };
    const saved = WALLS.find(([n]) => n === read());
    setWall(saved ? saved[1] : WALLS[0][1]);
    el.querySelectorAll('[data-wv-color]').forEach((b) => b.addEventListener('click', () => setWall(b.dataset.wvColor, b.dataset.name)));
    $('[data-wv-custom]').addEventListener('input', (e) => setWall(e.target.value));

    function draw() {
      const size = pdp.size;
      if (!size || el.hidden) return;
      const [a, b] = size.split('x').map(Number);
      const long = Math.max(a, b), short = Math.min(a, b);
      const land = ratio ? ratio >= 1 : b <= a;
      let w = land ? long : short, h = land ? short : long;
      const frame = pdp.hasFrame ? FRAMES[pdp.frame] || '#1d1d1d' : '';
      const mat = pdp.isPrint && pdp.mat !== 'No Mat' ? 2 : 0;
      const face = pdp.isPrint ? 1 : pdp.isFramed ? 1.25 : 0; // frame face + the floating gap, inches
      const W = w + 2 * (mat + face), H = h + 2 * (mat + face);
      const span = Math.max(150, (FLOOR + SOFA_H + GAP + H + GAP) / 0.6, W + 24);
      const x = (inch) => `${(inch / span) * 100}%`; // across
      const y = (inch) => `${(inch / (span * 0.6)) * 100}%`; // up
      wall.style.setProperty('--wv-floor', y(FLOOR));
      Object.assign($('.c365-wv__sofa').style, { width: x(SOFA_W), height: y(SOFA_H), bottom: y(FLOOR) });
      Object.assign(art.style, { width: x(W), height: y(H), bottom: y(FLOOR + SOFA_H + GAP) });
      art.style.setProperty('--wv-frame', frame || 'transparent');
      art.style.setProperty('--wv-face', x(face)); // padding % is of the wall's width, like x()
      art.style.setProperty('--wv-mat', `${(mat / (W - 2 * face)) * 100}%`);
      art.style.setProperty('--wv-mat-y', `${(mat / (H - 2 * face)) * 100}%`); // inset top/bottom % is of the height
      art.classList.toggle('is-framed', !!frame);
      $('[data-wv-sub]').textContent =
        `${Math.round(W)}×${Math.round(H)}″ ${pdp.isPrint ? L('Framed Print') : pdp.isFramed ? L('Framed Canvas') : T('wall.canvas', 'Canvas')}` +
        `${frame ? ` · ${T('wall.frame_value', '{frame} frame', { frame: L(pdp.frame) })}` : ''} · ${T('wall.above_sofa', 'above an 84″ sofa')}`;
    }
    const open = () => {
      const src = data.flatImage || pdp.querySelector('.c365-slide img')?.currentSrc;
      if (src && img.getAttribute('src') !== src) img.src = src;
      el.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      draw();
    };
    const close = () => { el.hidden = true; document.documentElement.style.overflow = ''; };
    openBtn.addEventListener('click', open);
    // "Looks great on" swatches (snippets/c365-wall-looks.liquid): the view in that wall color
    pdp.querySelectorAll('[data-wall-pick]').forEach((b) =>
      b.addEventListener('click', () => {
        setWall(b.dataset.wallPick, b.dataset.name);
        open();
      })
    );
    $('[data-wv-close]').addEventListener('click', close);
    el.addEventListener('click', (e) => { if (e.target === el) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !el.hidden) close(); });
    pdp.addEventListener('c365:change', draw);
  }

  // ---- artist bio: Read more ----
  pdp.querySelectorAll('[data-clamp]').forEach((p) => {
    const btn = p.nextElementSibling;
    if (!btn?.matches('[data-clamp-toggle]')) return;
    const check = () => { btn.hidden = p.scrollHeight <= p.clientHeight + 2 && !p.classList.contains('is-open'); };
    btn.addEventListener('click', () => {
      const open = p.classList.toggle('is-open');
      btn.textContent = open ? T('wall.read_less', 'Read less') : T('wall.read_more', 'Read more');
    });
    // the accordion body is hidden until opened: measure when it opens
    p.closest('[data-detail]')?.querySelector('.c365-detail__btn')?.addEventListener('click', () => requestAnimationFrame(check));
  });
})();
